import { forEach, find, includes, keys } from 'lodash';

import { Base, OwnedObject, forwardPropertiesFrom, invalidateOwner, areEqual, concat, cloneDeep } from 'gear';

import { Xml } from '../../util';
import { Point, Matrix } from '../../geometry';
import { Color, Brush, Pen } from '../../style';
import { Process } from '../../process/process';
import { Processes } from '../../process/processes';
import { Figure } from '../figure';
import { AnchorGenerator, AbsoluteAnchor, Pin, FigureTransform } from '../anchor';
import { TextProperties } from './properties';


/// <summary>
/// Two indices that denote a substring from an arbitrary-length string.
/// Positive indices start from 1 to indicate a position left-to-right.
///   For example, 2 in "abcd" indicates the 'b'.
/// Negative indices start from -1 to indicate a position right-to-left.
///   For example, -2 in "abcd" indicates the 'c'.
/// The value of 0 is reserved to indicate one position past the end of the string.
/// The two indices denote an open interval, thus:
///
///   TextRange(1,0).slice("abcd") == "abcd"
///   TextRange(1,0).slice("abcdefg") == "abcdefg"
///   TextRange(1,0).slice("abcd") == "abc"
///   TextRange(1,-1).slice("abcdefg") == "abcdef"
///   TextRange(-3,0).slice("abcd") == "bcd"
///   TextRange(-3,0).slice("abcdefg") == "efg"
///   TextRange(-3,-2).slice("abcd") == "b"
///   TextRange(-4,-2).slice("abcdefg") == "de"
///
/// If the second index corresponds to a position that is not past the first index,
/// the range represents an empty string:
///
///   TextRange(3,3).slice("abcd") == ""
///   TextRange(3,2).slice("abcd") == ""
///   TextRange(-3,-3).slice("abcd") == ""
///   TextRange(-3,-4).slice("abcd") == ""
///
/// If the first index corresponds to a position outside the string,
/// the range represents an empty string:
///
///   TextRange(5,0).slice("abcd") == ""
///   TextRange(1000,0).slice("abcd") == ""
///
/// If the second index corresponds to a position outside the string,
/// it indicates the one position right past the end of the string,
/// so it becomes equivalent to the value 0
///
///   TextRange(1,0).slice("abcdefg") == "abcdefg"
///   TextRange(1,8).slice("abcdefg") == "abcdefg"
///   TextRange(1,9).slice("abcdefg") == "abcdefg"
///   TextRange(1,1000).slice("abcdefg") == "abcdefg"
/// </summary>

export const TextRange = Figure.TextRange = OwnedObject.extend({ typeName: 'TextRange', __debugProps: true,

  init( p = {} ) {
    this.callParent();
    this.start = p.start || 1;
    this.end   = p.end   || 0;
  },

  slice( string ) {
    const len = string.length, start = this.start, end = this.end   ;

    const from = start < 0 ? Math.max(0,len+start) :
               start > 0 ? Math.min(start-1,len-1) : 0 ;

    const to = end < 0 ? Math.max(1,len+end) :
             end > 0 ? Math.min(end-1,len) : len ;

    return to-from > 0 ? string.substring(from,to) : '' ;
  },

  setupXml( node ) {
    const rangeString = this.start + ' ' + this.end;
    node.setAttribute('TextRange',rangeString);
  },

  clone() {
    return new TextRange({start:this.start,end:this.end});
  },
  isEqual( other ) {
    return this.start === other.start &&
           this.end === other.end;
  }
});

TextRange.parseXml = function( node ) {
  const rangeString = node.getAttribute('TextRange');
  return TextRange.parseXmlString( rangeString ) ;
};
TextRange.parseXmlString = function( string ) {
  const p = {};
  if( string ) {
    const l = string.split(' ');
    p.start = parseInt( l[0] );
    p.end   = parseInt( l[1] );
  }
  return new TextRange(p);
};
TextRange.LastLetter = function() { return new TextRange({start:-1,end:0}); };

Xml.Parser['TextRange'] = Xml.makeAttrParser(TextRange);



const textPartPowerClipLink = function(textPart,parent){
  /*
  const prePin = parent._computeInitialPrePin();
  const figureMatrix =  parent._computeMatrix(prePin);
  const cm = Mx.multiply( parentMatrix, figureMatrix );
  const pcMatrix = Mx.multiply(cm,Mx.translate(prePin));
  */

  const { powerClipContents, powerClipLink } = parent;

  if( powerClipLink ){
    // console.log('Transform PC-Link: ' + pcMatrix.textualize() + ' , prePin: ' + prePin.textualize() );
    return powerClipLink.clone();
  }
  else {
    const { powerClipId } = textPart;
    if( powerClipId ){
      const pcContent = find(powerClipContents,{ id: powerClipId });
      if( pcContent ){
        // console.log('Build PC-Link: ' + pcMatrix.textualize() + ' , prePin: ' + prePin.textualize() );
        return new Base.Type.PowerClipLink({ content: pcContent });
      }
    }
  }
};

// TextPart
//----------------------------------------------------------------------------------------

export const TextPart = Figure.TextPart = OwnedObject.extend(forwardPropertiesFrom(TextProperties,'properties',{ typeName: 'TextPart', __debugProps: true,

  Properties: {
    pen         : { type: 'Pen'      , def: Pen  .empty, owned: true, onChange: invalidateOwner },
    brush       : { type: 'Brush'    , def: Brush.empty, owned: true, onChange: invalidateOwner },
    anchor      : { type: 'Anchor'   , def: new AnchorGenerator(), owned: true, onChange: invalidateOwner },
    preMatrix22 : { type: 'Matrix22' , def: new Matrix(), manualParsing: true, onChange: invalidateOwner },
    matrix22    : { type: 'Matrix22' , def: new Matrix(), manualParsing: true, onChange: invalidateOwner },
    textRange   : { type: 'TextRange', def: new TextRange(), owned: true, onChange: invalidateOwner },
    processes   : { type: 'Process[]', def: [], owned: true,
                    onChange: invalidateOwner, array: Processes },
    properties: { type: 'TextProperties', owned: true, def: null, manualParsing: true, onChange: invalidateOwner }
  },

  get pin() {
    return this._pin.getPos();
  },
  set pin( pin_ ) {
    this._pin = this._pin.cloneElsewhere(pin_);
  },

  translate( x,y) {
    const p = y === undefined ? x : new Point(x,y);
    this.pin = this.pin .plus( p );
    return this;
  },

  // [TODO] Once pin is simplified, this will be the standard clone
  clone() {
    return new TextPart({
      pen: this._pen.clone(),
      brush: this._brush.clone(),
      anchor: this._anchor.clone(),
      preMatrix22: this._preMatrix22.clone(),
      matrix22: this._matrix22.clone(),
      textRange: this._textRange.clone(),
      processes: this._processes.cloneDeep(),
      properties: this._properties.clone(),
      pin: this._pin.clone()
    });
  },

  outputsText() {
    return true;
  },
  getColors( colors = [] ) {
    this._brush.getColors(colors);
    this._pen.getColors(colors);
    this._processes.getColors(colors);
    return colors;
  },
  getPens( pens = [] ) {
    pens.push( this._pen );
    this._processes.getPens(pens);
    return pens;
  },
  getBrushes( brushes = [] ) {
    brushes.push( this._brush );
    this._processes.getBrushes(brushes);
    return brushes;
  },
  mergedProperties( parent ) {
    return {
      text: this._textRange.slice( parent.text ),
      properties: this._properties.merge(parent._properties),
      brush: Brush.def(this._brush, parent._brush),
      pen  : Pen  .def(this._pen  , parent._pen  ),
      processes: cloneDeep(this._processes),
      anchor: this._anchor.clone(),
      preMatrix22: this._preMatrix22.clone()
    };
  },
  preText( parent ) {
     const p = this.mergedProperties(parent);
     p.matrix22 = this.matrix22;
     p.pin = this.pin;
     p.powerClipLink = textPartPowerClipLink(this,parent);
     return new Figure.Text(p);
  },
  asTextBlock( parent ) {

    const p = this.mergedProperties(parent);

    // We partially support parents effects like Shadow and Contour
    // Envelope is outside the limits.
    p.processes = concat(cloneDeep(parent.processes),p.processes);

    // This is complex because we want to clone every user defined property
    // We should review if there is a better mechanism for this
    const pKeys = keys(p);
    forEach( parent.Properties, (prop,name) => {
      if( prop.manualCloning!==true &&
          name !== 'cacheId' && name !== 'textParts' &&
          ! includes(pKeys,name) ) {
        const v = parent[prop.member];
        p[name] = v!==null ? v.cloneDeep ? v.cloneDeep() : v.clone ? v.clone() : v : null;
      }
    });

    const text = new Figure.Text(p);

    Figure._ungroup(text,parent,this);
    text.invalidate();

    text._textPartSource = this;
    return text;
  },
  replaceWithText( text, parent ) {

    this.properties = text._properties;
    this.brush = text._brush;
    this.pen = text._pen;

    this.processes = text._processes;

    this.anchor = text.anchor.clone();
    this.preMatrix22 = text.preMatrix22.clone();

    Figure._group(this,parent,text);
  },

  init( p = {} ) {
    if( ! p.properties ) {
      this.properties = new TextProperties();
    }
    this._pin = new Pin(p.pin);
    if( !p.anchor && ! p._anchor ) {
      this.anchor = new AnchorGenerator({ pos: new Point(0.5,0), absolute: false }); // [TODO]
    }
  },

  buildXml( parentNode ) {
    Xml.addObject(parentNode,this,'TextPart');
  },
  setupXml( node ) {
    Xml.setupProperties(node,this);
    this._properties.setupXml(node);
    let preMatrix22 = this._preMatrix22;
    if( ! preMatrix22.isIdentity() ) {
      const preTransformNode = Xml.appendElement(node,'PreTransform');
      preMatrix22.setMatrix22Attr(preTransformNode,'Matrix22');
    }
    FigureTransform.buildXml(this._matrix22,node);
    this._pin.buildXml(node);
  }

}));

TextPart.arrayFromXml = function(parentNode ) {
  return Xml.getObjectArray(parentNode,TextPart,'TextPart') ;
};
TextPart.parseXml = function( node ) {
  const p = Xml.parseProperties(node,TextPart);
  p.properties = TextProperties.parseXml(node);
  const preTransformNode = Xml.child(node,'PreTransform');
  if( preTransformNode ) {
    p.preMatrix22 = Matrix.getMatrix22Attr(preTransformNode, 'Matrix22');
  }
  p.matrix22 = FigureTransform.fromXml(node).matrix;
  p.pin      = Pin.fromXml(node) ;
  return new TextPart(p);
};
TextPart.createFromText = function(text,range,parentMatrix22,parentPin) {

  const tp = new TextPart({
    properties: text._properties.clone(),
    brush: text._brush,
    pen: text._pen,
    textRange: range,
    anchor: text._anchor.clone(),
    preMatrix22: text._preMatrix22.clone(),
    matrix22: text._matrix22.clone(),
    pin: text.pin.clone()
  });

  // Force anchor to absolute (0,0), right now it is the only anchor supported
  // by multi part and groups. We need to review this.
  const parentAnchor = AbsoluteAnchor(new Point(0,0));
  Figure._group( tp, { anchor:   parentAnchor,
                       matrix22: parentMatrix22,
                       pin:      parentPin       }, text );

  // The processes needs to know from what kind of figure they are being called
  // so this has to be done after creation, we can review how to improve this code
  tp.processes = text._processes.cloneDeep(tp);
  return tp;
};

// Are equal exept for pin, brush and pen
TextPart.areClones = function( tp1, tp2 ) {
  return tp1._textRange.isEqual(tp2._textRange) &&
         tp1._anchor.isEqual(tp2._anchor) &&
         tp1._preMatrix22.isEqual(tp2._preMatrix22) &&
         tp1._matrix22.isEqual(tp2._matrix22) &&
         areEqual(tp1._processes,tp2._processes) &&
         tp1._properties.isEqual(tp2._properties);
};

Figure.simplifyTextParts = function( figure ) {
  const changeset = Figure.findTextPartsChangeSet(figure);
  if(changeset.parts.length > 0) {
    let text = figure.clone();
    const textParts = text._textParts;
    changeset.parts.sort( (a,b) => b - a ); // Descending order
    forEach( changeset.parts, p => {
      textParts.splice(p,1);
    });
    if(textParts.length === 1) {
      const part = textParts[0];
      const range = part._textRange;
      if(range.start === 1 && range.end === 0) {
        // We can convert this text into a single line text
        text = part.asTextBlock(text);
      }
    }
    text._processes.add(changeset.processes);
    return text;
  }
  else {
    return figure.clone();
  }
};

Figure.textPartsCanBeSimplified = function ( figure ) {
  return figure.isText &&
         Figure.findTextPartsChangeSet(figure).parts.length > 0;
};

Figure.findTextPartsChangeSet = function( figure ) {
  const changeset = { parts: [], processes: [] };

  if( figure.isMultiPart ) {
    const parts = figure._textParts.clone(); parts.reverse();
    const clones = {};
    const mainParts = [];
    const grouped = [];
    let count = 0;
    let i=0, k=1, n = parts.length;
    for(; k < n ; ++i, ++k) {
      if( includes(grouped,i) ) {
        continue;
      }
      for(let j = k; j < n; ++j) {
        if( TextPart.areClones(parts[i], parts[j]) ) {
          if( ! clones[i] ) {
            clones[i] = [];
            mainParts.push(i);
            ++count;
          }
          clones[i].push(j);
          grouped.push(j);
          ++count;
        }
      }
    }
    if( count !== n ) { return changeset; }

    let maxMainPenWidth = 0;
    forEach( mainParts, pi => {
      const pw = parts[pi].pen._width;
      if( pw > maxMainPenWidth ) {
        maxMainPenWidth = pw ;
      }
    });

    const cn = mainParts.length;
    const mc = clones[mainParts[0]];

    // Every part must share the same final transform
    const matrix22 = parts[mainParts[0]]._matrix22;
    const invMatrix22 = matrix22.inverted();

    for(let t = mc.length-1; t >= 0; --t) {
      const parts_mc_t = parts[mc[t]];
      const pinOffset0 = parts_mc_t.pin.transformed(invMatrix22) .minus(
                         parts[mainParts[0]].pin.transformed(invMatrix22) );
      const brush = parts_mc_t._brush;
      const pen   = parts_mc_t._pen;

      let mainParts_l, l;
      for(l = 1; l < cn; ++l) {
        mainParts_l = mainParts[l];
        const oc = clones[mainParts_l];
        if( t >= oc.length ) {
          return changeset;
        }
        const parts_oc_t = parts[oc[t]];

        if( ! matrix22.isEqual(parts_oc_t._matrix22) ) {
          return changeset;
        }

        const pinOffsetl = parts_oc_t.pin.transformed(invMatrix22) .minus(
                             parts[mainParts_l].pin.transformed(invMatrix22) );

        if( ! pinOffset0.isNear(pinOffsetl,0.05) ) { // do not mind a little bit of offset
          return changeset;
        }

        const equalStyle = brush.isEqual(parts_oc_t._brush) &&
                         pen  .isNear(parts_oc_t._pen,0.05); // do not mind a little bit of difference
        if( ! equalStyle ) {
          return changeset;
        }
      }

      // We can represent these parts as a single process
      const changeParts = [];
      for(let d = 0; d < cn; ++d) {
        const index = parts.length-1 - clones[mainParts[d]][t];
        changeParts.push(index);
      }
      const outline = (pinOffset0.isEqual(new Point(0, 0)));
      if( ! outline ) {
        if( changeset.processes.length > 0 ) {
          // Shadow can only be the last process
          // Abort the changes
          return { parts: [], processes: [] };
        }
        const process = new Process.RasterShadow({
          offsetX:pinOffset0.x, offsetY:pinOffset0.y,
          // The shadow process modifies the pen when it is empty
          pen: pen._empty ? Pen({width:0,color:Color.Transparent()}) : pen,
          brush: brush
        });

        changeset.processes.push(process);
        changeset.parts = changeset.parts.concat(changeParts);
      }
      else {
        // We keep extending the pen contours
        let pc = changeset.processes[0];
        if( ! (pc instanceof Process.PenContour) ) {
          pc = new Process.PenContour({pens:[]});
          changeset.processes.unshift(pc);
        }
        const pw = pen._width - maxMainPenWidth;
        // Correct other pen widths, they are accumulative in PenContour
        const pc_pens = pc._pens;
        forEach( pc_pens, p => {
          p.width = p._width-pw;
        });
        const cpen = pen.clone();
        cpen.width = pw;
        pc_pens.unshift(cpen);
        changeset.parts = changeset.parts.concat(changeParts);
      }
    }
  }

  return changeset;
};



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/text/multi-part.js
 **/